import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { fetchBlob, downloadFile } from '../api/client'
import {
  useDeleteDiagram,
  useDiagram,
  useProjectFiles,
  useSaveDiagram,
  useSaveDiagramPdf,
  useUploadProjectFile
} from '../api/queries'
import { fileCategoryLabel, type DiagramContent, type DiagramElement, type DiagramHeader } from '../api/types'
import { Card, ErrorBox, Loading } from '../components/ui'
import { formatDateTime } from '../lib/format'

/**
 * Diagramværktøjet (beslutning 150): træk symboler ud på tegnefladen og byg et enstregsdiagram som husets
 * regneark. Tegningen gemmes som data på sagen, så den kan rettes igen; "Gem som PDF i sagen" lægger PDF'en i
 * sagens Dokumenter-mappe. Koordinaterne er de samme som i DiagramRules på serveren (1600 x 900).
 */

const W = 1600
const H = 900
const GRID = 10
const BLUE = '#4472C4'

type El = DiagramElement & { id: string }
type Drag =
  | { mode: 'move'; id: string; startX: number; startY: number; origin: El }
  | { mode: 'label'; id: string; startX: number; startY: number; origin: El }
  | { mode: 'p1' | 'p2' | 'resize'; id: string; origin: El }

let nextId = 1
const newId = () => `e${nextId++}`
const snap = (v: number) => Math.round(v / GRID) * GRID

/** Hvor tæt (i tegningens enheder) noget skal være, før det sætter sig fast. */
const STICK = 16

type Point = { x: number; y: number }

/** Hjælpelinjer, der viser, hvad et symbol eller en ende sidder fast på, mens man trækker. */
type Guides = { x?: number; y?: number; point?: Point }

/**
 * Et symbols tilslutninger: midt på venstre og højre side (afbryderen i højde med sin linje) og midt foroven og
 * forneden. Et kabels er dets to ender. Tekst har ingen.
 */
function ports(e: DiagramElement): Point[] {
  if (e.kind === 'line') return [{ x: e.x, y: e.y }, { x: e.x2 ?? e.x, y: e.y2 ?? e.y }]
  if (e.kind === 'text') return []
  const w = e.w ?? 0
  const h = e.h ?? 0
  const mid = e.kind === 'breaker' ? e.y + h * 0.75 : e.y + h / 2
  return [
    { x: e.x, y: mid },
    { x: e.x + w, y: mid },
    { x: e.x + w / 2, y: e.y },
    { x: e.x + w / 2, y: e.y + h }
  ]
}

/** Det nærmeste punkt på et vandret eller lodret kabel (en samleskinne) - eller null, hvis det er skråt eller for langt væk. */
function onStraightLine(p: Point, line: DiagramElement): Point | null {
  if (line.kind !== 'line') return null
  const x2 = line.x2 ?? line.x
  const y2 = line.y2 ?? line.y
  if (line.y === y2 && p.x >= Math.min(line.x, x2) && p.x <= Math.max(line.x, x2)) return { x: snap(p.x), y: line.y }
  if (line.x === x2 && p.y >= Math.min(line.y, y2) && p.y <= Math.max(line.y, y2)) return { x: line.x, y: snap(p.y) }
  return null
}

/** Hvor en kabelende skal sætte sig fast: en tilslutning først, ellers et punkt på et andet kabel. */
function nearestConnection(p: Point, others: DiagramElement[]): Point | null {
  let best: Point | null = null
  let bestDistance = STICK
  const consider = (c: Point | null) => {
    if (!c) return
    const d = Math.hypot(c.x - p.x, c.y - p.y)
    if (d < bestDistance) {
      best = c
      bestDistance = d
    }
  }
  for (const e of others) for (const c of ports(e)) consider(c)
  if (best) return best
  for (const e of others) consider(onStraightLine(p, e))
  return best
}

/**
 * Hvor meget et flyttet symbol skal skubbes, så en af dets tilslutninger kommer på præcis samme højde som et vandret
 * kabel eller en anden tilslutning - og tilsvarende lodret. Sådan kommer M, afbryder og inverter på én lige linje.
 */
function alignTo(own: Point[], others: DiagramElement[]): { dx: number; dy: number; guides: Guides } {
  let dy = 0
  let dx = 0
  let bestY = STICK
  let bestX = STICK
  const guides: Guides = {}

  for (const e of others) {
    const targets = ports(e)
    const x2 = e.x2 ?? e.x
    const y2 = e.y2 ?? e.y
    for (const p of own) {
      for (const t of targets) {
        if (Math.abs(t.y - p.y) < bestY) { bestY = Math.abs(t.y - p.y); dy = t.y - p.y; guides.y = t.y }
        if (Math.abs(t.x - p.x) < bestX) { bestX = Math.abs(t.x - p.x); dx = t.x - p.x; guides.x = t.x }
      }
      // Et vandret kabel trækker i højden, en lodret samleskinne i siden - hele vejen langs dem.
      if (e.kind === 'line' && e.y === y2 && Math.abs(e.y - p.y) < bestY) { bestY = Math.abs(e.y - p.y); dy = e.y - p.y; guides.y = e.y }
      if (e.kind === 'line' && e.x === x2 && Math.abs(e.x - p.x) < bestX) { bestX = Math.abs(e.x - p.x); dx = e.x - p.x; guides.x = e.x }
    }
  }

  if (bestY === STICK) delete guides.y
  if (bestX === STICK) delete guides.x
  return { dx, dy, guides }
}
const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v))

interface PaletteItem {
  key: string
  label: string
  make: (x: number, y: number) => DiagramElement
}

/** Symbolerne fra husets enstregsdiagrammer. */
const PALETTE: PaletteItem[] = [
  { key: 'meter', label: 'Måler', make: (x, y) => ({ kind: 'meter', x: x - 35, y: y - 30, w: 70, h: 60, label: 'Afregningsmåler' }) },
  { key: 'breaker', label: 'Afbryder', make: (x, y) => ({ kind: 'breaker', x: x - 45, y: y - 25, w: 90, h: 50, label: '250A 4P + RCD type B' }) },
  { key: 'inverter', label: 'Inverter', make: (x, y) => ({ kind: 'inverter', x: x - 35, y: y - 30, w: 70, h: 60, label: 'EPCS125-AM' }) },
  { key: 'box', label: 'Batteri / kasse', make: (x, y) => ({ kind: 'box', x: x - 130, y: y - 30, w: 260, h: 60, label: '1 Batteri 125 kW/ 261kWh' }) },
  { key: 'line', label: 'Kabel', make: (x, y) => ({ kind: 'line', x: x - 100, y, x2: x + 100, y2: y, label: '4x150mm2 AL' }) },
  { key: 'vline', label: 'Lodret kabel', make: (x, y) => ({ kind: 'line', x, y: y - 100, x2: x, y2: y + 100 }) },
  { key: 'busbar', label: 'Samleskinne', make: (x, y) => ({ kind: 'line', x, y: y - 150, x2: x, y2: y + 150, thick: true }) },
  { key: 'border', label: 'Ejergrænse', make: (x, y) => ({ kind: 'line', x, y: y - 200, x2: x, y2: y + 200, dashed: true, label: 'Ejergrænse' }) },
  { key: 'arrow', label: 'Pil', make: (x, y) => ({ kind: 'line', x: x - 100, y, x2: x + 100, y2: y, arrow: true, label: 'Forbrug' }) },
  { key: 'text', label: 'Tekst', make: (x, y) => ({ kind: 'text', x: x - 40, y: y - 20, label: 'Tekst', size: 18 }) },
  { key: 'highlight', label: 'Gul markering', make: (x, y) => ({ kind: 'text', x: x - 40, y: y - 20, label: 'begrænset effekt', size: 18, highlight: true }) }
]

/** Holder et element inden for tegnefladen - serveren afviser alt uden for. */
function fit(e: El): El {
  if (e.kind === 'line') {
    return { ...e, x: clamp(e.x, 0, W), y: clamp(e.y, 0, H), x2: clamp(e.x2 ?? e.x, 0, W), y2: clamp(e.y2 ?? e.y, 0, H) }
  }
  const w = e.w ?? 0
  const h = e.h ?? 0
  return { ...e, x: clamp(e.x, 0, W - w), y: clamp(e.y, 0, H - h) }
}

function withIds(content?: DiagramContent | null): El[] {
  return (content?.elements ?? []).map((e) => ({ ...e, id: newId() }))
}

const stripIds = (elements: El[]): DiagramElement[] =>
  elements.map(({ id: _id, ...rest }) => rest)

export function DiagramEditorPage() {
  const id = Number(useParams().id)
  const { data, isLoading, error } = useDiagram(id)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data) return null

  // key: et genindlæst diagram starter forfra med de gemte data.
  return <Editor key={data.summary.id} diagramId={id} initial={data} />
}

function Editor({ diagramId, initial }: { diagramId: number; initial: NonNullable<ReturnType<typeof useDiagram>['data']> }) {
  const navigate = useNavigate()
  const save = useSaveDiagram(diagramId)
  const savePdf = useSaveDiagramPdf(diagramId)
  const remove = useDeleteDiagram()
  const summary = initial.summary

  const [header, setHeader] = useState<DiagramHeader>(() => ({
    title: initial.content.header?.title ?? summary.title,
    address: initial.content.header?.address ?? initial.projectAddress ?? '',
    installNumber: initial.content.header?.installNumber ?? '',
    installer: initial.content.header?.installer ?? '',
    extra: initial.content.header?.extra ?? ''
  }))
  const [elements, setElements] = useState<El[]>(() => withIds(initial.content))
  const [selected, setSelected] = useState<string | null>(null)
  const [drag, setDrag] = useState<Drag | null>(null)
  const [guides, setGuides] = useState<Guides>({})
  // "Tegn kabel": træk på tegningen fra ét punkt til et andet. "Fri vinkel" tillader skrå kabler.
  const [drawing, setDrawing] = useState(false)
  const [freeAngle, setFreeAngle] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)
  const history = useRef<string[]>([])
  const svgRef = useRef<SVGSVGElement>(null)

  const current = useMemo(() => JSON.stringify({ header, elements: stripIds(elements) }), [header, elements])
  const [savedJson, setSavedJson] = useState(current)
  const dirty = current !== savedJson

  // Advarsel, hvis man lukker siden med ændringer, der ikke er gemt.
  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  function remember() {
    history.current.push(JSON.stringify(elements))
    if (history.current.length > 100) history.current.shift()
  }

  function undo() {
    const previous = history.current.pop()
    if (previous) {
      setElements(JSON.parse(previous) as El[])
      setSelected(null)
    }
  }

  function update(id: string, changes: Partial<El>, record = true) {
    if (record) remember()
    setElements((list) => list.map((e) => (e.id === id ? fit({ ...e, ...changes }) : e)))
  }

  function add(item: PaletteItem, x: number, y: number) {
    remember()
    const element = fit({ ...item.make(snap(x), snap(y)), id: newId() })
    setElements((list) => [...list, element])
    setSelected(element.id)
  }

  function duplicate(id: string) {
    const source = elements.find((e) => e.id === id)
    if (!source) return
    remember()
    // Lige under originalen - så fire batterirækker hurtigt kan bygges af én.
    const offset = source.kind === 'line' ? 60 : (source.h ?? 40) + 20
    const copy = fit({ ...source, id: newId(), y: source.y + offset, y2: source.y2 != null ? source.y2 + offset : undefined })
    setElements((list) => [...list, copy])
    setSelected(copy.id)
  }

  function removeElement(id: string) {
    remember()
    setElements((list) => list.filter((e) => e.id !== id))
    setSelected(null)
  }

  // Tastatur: Delete, Ctrl+Z, Ctrl+D og piletaster - men ikke mens man skriver i et felt.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const target = event.target as HTMLElement
      if (target.closest('input, textarea, select')) return
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault()
        undo()
        return
      }
      if (!selected) return
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault()
        removeElement(selected)
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'd') {
        event.preventDefault()
        duplicate(selected)
      } else if (event.key.startsWith('Arrow')) {
        event.preventDefault()
        const e = elements.find((x) => x.id === selected)
        if (!e) return
        const step = event.shiftKey ? GRID * 5 : GRID
        const dx = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0
        const dy = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0
        update(e.id, { x: e.x + dx, y: e.y + dy, x2: e.x2 != null ? e.x2 + dx : undefined, y2: e.y2 != null ? e.y2 + dy : undefined })
      } else if (event.key === 'Escape') {
        setSelected(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  function toSvg(clientX: number, clientY: number) {
    const svg = svgRef.current
    const matrix = svg?.getScreenCTM()
    if (!svg || !matrix) return { x: 0, y: 0 }
    const point = new DOMPoint(clientX, clientY).matrixTransform(matrix.inverse())
    return { x: point.x, y: point.y }
  }

  function startDrag(event: ReactPointerEvent, element: El, mode: Drag['mode']) {
    event.stopPropagation()
    event.preventDefault()
    setSelected(element.id)
    remember()
    try {
      // Fanger musen, så trækket følger med, selv om den kommer uden for symbolet.
      svgRef.current?.setPointerCapture(event.pointerId)
    } catch {
      // Ikke alle browsere tillader det - trækket virker stadig inden for tegningen.
    }
    const p = toSvg(event.clientX, event.clientY)
    setDrag(mode === 'move' || mode === 'label'
      ? { mode, id: element.id, startX: p.x, startY: p.y, origin: element }
      : { mode, id: element.id, origin: element })
  }

  function onPointerMove(event: ReactPointerEvent) {
    if (!drag) return
    const p = toSvg(event.clientX, event.clientY)
    const o = drag.origin
    const others = elements.filter((e) => e.id !== o.id)
    if (drag.mode === 'move') {
      let dx = snap(p.x - drag.startX)
      let dy = snap(p.y - drag.startY)
      // Symbolet sætter sig på linjen: kommer en af dets tilslutninger tæt på et kabel eller en anden tilslutning,
      // rettes det ind på præcis samme højde (eller samme lodrette linje).
      const moved = { ...o, x: o.x + dx, y: o.y + dy, x2: o.x2 != null ? o.x2 + dx : undefined, y2: o.y2 != null ? o.y2 + dy : undefined }
      const align = alignTo(ports(moved), others)
      dx += align.dx
      dy += align.dy
      setGuides(align.guides)
      update(o.id, { x: o.x + dx, y: o.y + dy, x2: o.x2 != null ? o.x2 + dx : undefined, y2: o.y2 != null ? o.y2 + dy : undefined }, false)
    } else if (drag.mode === 'label') {
      // Kun teksten flyttes - i små trin, så den kan lægges præcist.
      const step = (v: number) => Math.round(v / 5) * 5
      update(o.id, { lx: (o.lx ?? 0) + step(p.x - drag.startX), ly: (o.ly ?? 0) + step(p.y - drag.startY) }, false)
    } else if (drag.mode === 'resize') {
      update(o.id, { w: Math.max(20, snap(p.x - o.x)), h: Math.max(20, snap(p.y - o.y)) }, false)
    } else {
      const other = drag.mode === 'p1' ? { x: o.x2 ?? o.x, y: o.y2 ?? o.y } : { x: o.x, y: o.y }
      // Kabler er vandrette eller lodrette, medmindre "Fri vinkel" er slået til eller Alt holdes nede:
      // den retning, man trækker mest i, vinder.
      const free = freeAngle || event.altKey
      const horizontal = Math.abs(p.x - other.x) >= Math.abs(p.y - other.y)
      let x = snap(p.x)
      let y = snap(p.y)
      if (!free) {
        if (horizontal) y = other.y
        else x = other.x
      }
      // Enden sætter sig fast på en tilslutning eller på et andet kabel / en samleskinne - når kablet er låst, kun
      // hvis det stadig bliver lige.
      const hit = nearestConnection({ x: free ? p.x : x, y: free ? p.y : y }, others)
      const straight = hit && (free || (horizontal ? Math.abs(hit.y - other.y) < 0.5 : Math.abs(hit.x - other.x) < 0.5))
      if (hit && straight) {
        x = hit.x
        y = hit.y
        setGuides({ point: hit })
      } else {
        setGuides(free ? {} : horizontal ? { y } : { x })
      }
      update(o.id, drag.mode === 'p1' ? { x, y } : { x2: x, y2: y }, false)
    }
  }

  function endDrag() {
    // Et klik uden træk med "Tegn kabel" må ikke efterlade et kabel uden længde.
    if (drag) {
      setElements((list) =>
        list.filter((e) => !(e.id === drag.id && e.kind === 'line' && e.x === e.x2 && e.y === e.y2))
      )
    }
    setDrag(null)
    setGuides({})
  }

  async function persist() {
    const title = header.title?.trim() || 'Diagram'
    const saved = await save.mutateAsync({ title, content: { header: { ...header, title }, elements: stripIds(elements) } })
    setSavedJson(current)
    return saved
  }

  async function onSave() {
    setMessage(null)
    try {
      await persist()
      setMessage({ ok: true, text: 'Diagrammet er gemt.' })
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'Diagrammet kunne ikke gemmes.' })
    }
  }

  async function onSavePdf() {
    setMessage(null)
    try {
      if (dirty) await persist()
      await savePdf.mutateAsync()
      setMessage({ ok: true, text: 'Diagrammet er gemt som PDF i sagens Dokumenter-mappe.' })
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'PDF\'en kunne ikke gemmes i sagen.' })
    }
  }

  async function onPreview() {
    setMessage(null)
    // Vinduet åbnes med det samme - ellers blokerer browseren det, fordi det sker efter et kald.
    const preview = window.open('', '_blank')
    try {
      if (dirty) await persist()
      const blob = await fetchBlob(`/api/diagrams/${diagramId}/pdf`)
      if (!blob) throw new Error('PDF\'en kunne ikke laves.')
      const url = URL.createObjectURL(blob)
      if (preview) preview.location.href = url
      else window.open(url, '_blank')
    } catch (err) {
      preview?.close()
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'PDF\'en kunne ikke vises.' })
    }
  }

  async function onDelete() {
    if (!window.confirm(`Slet diagrammet "${summary.title}"? En PDF, der allerede ligger i sagens mappe, bliver liggende.`)) return
    try {
      await remove.mutateAsync(diagramId)
      navigate(`/projects/${summary.projectId}`)
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'Diagrammet kunne ikke slettes.' })
    }
  }

  const sel = elements.find((e) => e.id === selected) ?? null
  const busy = save.isPending || savePdf.isPending

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="row row--between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="small">
          <Link to={`/projects/${summary.projectId}`}>
            ← {summary.projectOrderNumber ? `Ordre ${summary.projectOrderNumber} · ` : ''}
            {summary.projectName}
          </Link>
          <span className="muted"> · Rettet {formatDateTime(summary.updatedAt)}{summary.updatedBy ? ` af ${summary.updatedBy}` : ''}</span>
        </div>
        <div className="btn-group" style={{ flexWrap: 'wrap' }}>
          <button className="btn btn--primary" disabled={busy || !dirty} onClick={() => void onSave()}>
            {save.isPending ? 'Gemmer ...' : dirty ? '💾 Gem' : '✓ Gemt'}
          </button>
          <button className="btn" disabled={busy} onClick={() => void onSavePdf()}>
            {savePdf.isPending ? 'Laver PDF ...' : '📄 Gem som PDF i sagen'}
          </button>
          <button className="btn" disabled={busy} onClick={() => void onPreview()}>👁 Vis PDF</button>
          <button className="btn btn--danger" disabled={busy} onClick={() => void onDelete()}>Slet</button>
        </div>
      </div>

      {message && <div className={message.ok ? 'success-box' : 'error-box'}>{message.text}</div>}
      {summary.uploadError && !message && (
        <div className="error-box">Seneste PDF kom ikke i sagens mappe: {summary.uploadError}</div>
      )}
      {summary.pdfSavedAt && (
        <div className="tiny muted">Senest gemt som PDF i sagen {formatDateTime(summary.pdfSavedAt)}.</div>
      )}

      <Card>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Overskrift</span>
            <input value={header.title ?? ''} maxLength={200} onChange={(e) => setHeader({ ...header, title: e.target.value })} />
          </label>
          <label className="field">
            <span className="field__label">Installationsadresse</span>
            <input value={header.address ?? ''} maxLength={200} onChange={(e) => setHeader({ ...header, address: e.target.value })} />
          </label>
          <label className="field">
            <span className="field__label">Inst nr.</span>
            <input value={header.installNumber ?? ''} maxLength={200} placeholder="Står med gult, til det er udfyldt" onChange={(e) => setHeader({ ...header, installNumber: e.target.value })} />
          </label>
          <label className="field">
            <span className="field__label">El-installatør</span>
            <input value={header.installer ?? ''} maxLength={200} onChange={(e) => setHeader({ ...header, installer: e.target.value })} />
          </label>
          <label className="field">
            <span className="field__label">Ekstra linje</span>
            <input value={header.extra ?? ''} maxLength={200} placeholder="F.eks. t-ret: 330kW" onChange={(e) => setHeader({ ...header, extra: e.target.value })} />
          </label>
        </div>
      </Card>

      <div className="diagram-editor">
        <div className="diagram-palette" aria-label="Symboler">
          <button
            type="button"
            className={`btn btn--sm${drawing ? ' btn--primary' : ''}`}
            aria-pressed={drawing}
            onClick={() => setDrawing((v) => !v)}
            title="Træk på tegningen fra ét punkt til et andet"
          >
            ✏️ {drawing ? 'Tegner kabler - tryk for at stoppe' : 'Tegn kabel'}
          </button>
          <label className="row tiny" style={{ gap: 6, marginBottom: 6 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={freeAngle} onChange={(e) => setFreeAngle(e.target.checked)} />
            Fri vinkel (ellers altid vandret eller lodret)
          </label>
          <div className="tiny muted" style={{ marginBottom: 4 }}>Træk ud på tegningen - eller tryk</div>
          {PALETTE.map((item) => (
            <button
              key={item.key}
              type="button"
              className="diagram-palette__item"
              draggable
              onDragStart={(e) => e.dataTransfer.setData('text/plain', item.key)}
              onClick={() => add(item, W / 2, H / 2)}
              title={`${item.label} - træk ud på tegningen`}
            >
              <svg viewBox="0 0 60 40" width={42} height={28} aria-hidden>
                <PaletteIcon kind={item.key} />
              </svg>
              <span>{item.label}</span>
            </button>
          ))}
          <div className="tiny muted" style={{ marginTop: 8 }}>
            Ctrl+Z fortryder · Ctrl+D kopierer · Delete sletter · piletaster flytter
          </div>
        </div>

        <div className="diagram-canvas">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${W} ${H}`}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onPointerDown={(event) => {
              if (!drawing) {
                setSelected(null)
                return
              }
              // Et nyt kabel starter her - fast på en tilslutning, hvis der er en tæt på - og trækkes ud med enden.
              const p = toSvg(event.clientX, event.clientY)
              const start = nearestConnection(p, elements) ?? { x: snap(p.x), y: snap(p.y) }
              const line = fit({ kind: 'line', x: start.x, y: start.y, x2: start.x, y2: start.y, id: newId() })
              setElements((list) => [...list, line])
              startDrag(event, line, 'p2')
            }}
            style={{ width: '100%', height: 'auto', display: 'block', touchAction: 'none', background: '#fff', cursor: drawing ? 'crosshair' : undefined }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault()
              const item = PALETTE.find((p) => p.key === e.dataTransfer.getData('text/plain'))
              if (!item) return
              const p = toSvg(e.clientX, e.clientY)
              add(item, p.x, p.y)
            }}
          >
            <defs>
              <pattern id="diagram-grid" width={GRID * 5} height={GRID * 5} patternUnits="userSpaceOnUse">
                <path d={`M ${GRID * 5} 0 L 0 0 0 ${GRID * 5}`} fill="none" stroke="#E5E7EB" strokeWidth={1} />
              </pattern>
              <marker id="diagram-arrow" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={5} markerHeight={5} orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill={BLUE} />
              </marker>
            </defs>
            <rect width={W} height={H} fill="url(#diagram-grid)" />
            {elements.map((e) => (
              <Shape
                key={e.id}
                e={e}
                selected={e.id === selected}
                onMove={(ev) => startDrag(ev, e, 'move')}
                onHandle={(ev, mode) => startDrag(ev, e, mode)}
              />
            ))}
            {/* Mens man trækker: tilslutningerne som små prikker og en orange linje dér, hvor noget sidder fast.
                Tegnes kun på skærmen - aldrig i PDF'en. */}
            {drag && (
              <g pointerEvents="none">
                {elements
                  .filter((e) => e.id !== drag.id && e.kind !== 'line')
                  .flatMap((e) => ports(e).map((p, i) => <circle key={`${e.id}-${i}`} cx={p.x} cy={p.y} r={4} fill="#F97316" opacity={0.6} />))}
                {guides.y != null && <line x1={0} y1={guides.y} x2={W} y2={guides.y} stroke="#F97316" strokeWidth={1.5} strokeDasharray="8 6" />}
                {guides.x != null && <line x1={guides.x} y1={0} x2={guides.x} y2={H} stroke="#F97316" strokeWidth={1.5} strokeDasharray="8 6" />}
                {guides.point && <circle cx={guides.point.x} cy={guides.point.y} r={12} fill="none" stroke="#F97316" strokeWidth={3} />}
              </g>
            )}
          </svg>
          {elements.length === 0 && (
            <div className="diagram-canvas__empty">Træk et symbol ud fra listen til venstre for at begynde.</div>
          )}
        </div>

        <div className="stack" style={{ gap: 12 }}>
          <Card title={sel ? 'Valgt symbol' : 'Intet valgt'}>
            {sel ? (
              <Properties e={sel} onChange={(changes) => update(sel.id, changes)} onDuplicate={() => duplicate(sel.id)} onDelete={() => removeElement(sel.id)} />
            ) : (
              <p className="small muted" style={{ margin: 0 }}>Tryk på et symbol på tegningen for at rette teksten eller udseendet.</p>
            )}
          </Card>
          <DocumentsCard projectId={summary.projectId} />
        </div>
      </div>
    </div>
  )
}

function Properties({
  e,
  onChange,
  onDuplicate,
  onDelete
}: {
  e: El
  onChange: (changes: Partial<El>) => void
  onDuplicate: () => void
  onDelete: () => void
}) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      <label className="field">
        <span className="field__label">{e.kind === 'box' ? 'Tekst i kassen' : e.kind === 'text' ? 'Tekst' : 'Tekst over (Enter giver ny linje)'}</span>
        {e.kind !== 'box' ? (
          <textarea rows={3} maxLength={200} value={e.label ?? ''} onChange={(ev) => onChange({ label: ev.target.value })} />
        ) : (
          <input maxLength={200} value={e.label ?? ''} onChange={(ev) => onChange({ label: ev.target.value })} />
        )}
      </label>
      {e.kind === 'line' && (
        <div className="stack" style={{ gap: 4 }}>
          <label className="row small" style={{ gap: 6 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={!!e.dashed} onChange={(ev) => onChange({ dashed: ev.target.checked })} /> Stiplet
          </label>
          <label className="row small" style={{ gap: 6 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={!!e.thick} onChange={(ev) => onChange({ thick: ev.target.checked })} /> Tyk (samleskinne)
          </label>
          <label className="row small" style={{ gap: 6 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={!!e.arrow} onChange={(ev) => onChange({ arrow: ev.target.checked })} /> Pil for enden
          </label>
          <div className="btn-group">
            {/* Retter et skævt kabel ud - den første ende bliver, den anden flyttes ind på linjen. */}
            <button className="btn btn--sm" type="button" onClick={() => onChange({ y2: e.y })}>↔ Gør vandret</button>
            <button className="btn btn--sm" type="button" onClick={() => onChange({ x2: e.x })}>↕ Gør lodret</button>
          </div>
          <span className="tiny muted">Træk i de runde prikker for at flytte enderne. Træk i teksten for at flytte den.</span>
        </div>
      )}
      {e.kind === 'text' && (
        <div className="stack" style={{ gap: 4 }}>
          <label className="field">
            <span className="field__label">Størrelse</span>
            <select value={e.size ?? 18} onChange={(ev) => onChange({ size: Number(ev.target.value) })}>
              {[12, 14, 16, 18, 22, 28, 36, 48].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <label className="row small" style={{ gap: 6 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={!!e.bold} onChange={(ev) => onChange({ bold: ev.target.checked })} /> Fed
          </label>
          <label className="row small" style={{ gap: 6 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={!!e.highlight} onChange={(ev) => onChange({ highlight: ev.target.checked })} /> Gul markering
          </label>
        </div>
      )}
      {e.kind !== 'line' && e.kind !== 'text' && <span className="tiny muted">Træk i hjørnet nederst til højre for at ændre størrelsen.</span>}
      {e.kind !== 'text' && (e.lx || e.ly) ? (
        <button className="btn btn--sm" type="button" style={{ alignSelf: 'flex-start' }} onClick={() => onChange({ lx: 0, ly: 0 })}>
          Sæt teksten tilbage
        </button>
      ) : null}
      <div className="btn-group">
        <button className="btn btn--sm" type="button" onClick={onDuplicate}>Kopiér</button>
        <button className="btn btn--sm btn--danger" type="button" onClick={onDelete}>Fjern</button>
      </div>
    </div>
  )
}

/** Sagens dokumenter - de ligger i samme Dokumenter-mappe som diagrammets PDF. */
function DocumentsCard({ projectId }: { projectId: number }) {
  const { data: files } = useProjectFiles(projectId)
  const upload = useUploadProjectFile(projectId)
  const [error, setError] = useState<string | null>(null)
  const documents = (files ?? []).filter((f) => !f.contentType.startsWith('image/'))

  async function onFiles(list: FileList | null) {
    setError(null)
    for (const file of Array.from(list ?? [])) {
      try {
        await upload.mutateAsync({ file, fileName: file.name, category: 'other' })
      } catch (err) {
        setError(err instanceof Error ? err.message : `${file.name} kunne ikke lægges op.`)
      }
    }
  }

  return (
    <Card title={`Dokumenter på sagen (${documents.length})`}>
      <div className="stack" style={{ gap: 6 }}>
        {documents.map((f) => (
          <button
            key={f.id}
            type="button"
            className="link-button small"
            style={{ textAlign: 'left' }}
            onClick={() => void downloadFile(`/api/projects/${projectId}/files/${f.id}/content`, f.name)}
          >
            📄 {f.name} <span className="tiny muted">· {fileCategoryLabel(f.category)}</span>
          </button>
        ))}
        {documents.length === 0 && <span className="small muted">Ingen dokumenter endnu.</span>}
        <label className="btn btn--sm" style={{ alignSelf: 'flex-start', marginTop: 4 }}>
          {upload.isPending ? 'Lægger op ...' : '📎 Upload dokumenter'}
          <input type="file" multiple hidden onChange={(e) => { void onFiles(e.target.files); e.target.value = '' }} />
        </label>
        <span className="tiny muted">Lægges i sagens Dokumenter-mappe sammen med diagrammets PDF.</span>
        {error && <div className="error-box">{error}</div>}
      </div>
    </Card>
  )
}

/** Ét element på tegningen - tegnes som i PDF'en (DiagramPdfService). */
function Shape({
  e,
  selected,
  onMove,
  onHandle
}: {
  e: El
  selected: boolean
  onMove: (event: ReactPointerEvent) => void
  onHandle: (event: ReactPointerEvent, mode: 'p1' | 'p2' | 'resize' | 'label') => void
}) {
  const stroke = e.thick ? 7 : 4
  const w = e.w ?? 0
  const h = e.h ?? 0
  const label = e.label ?? ''
  const handle = (cx: number, cy: number, mode: 'p1' | 'p2' | 'resize') => (
    <circle cx={cx} cy={cy} r={9} fill="#fff" stroke="#F97316" strokeWidth={3} style={{ cursor: 'crosshair' }} onPointerDown={(ev) => onHandle(ev, mode)} />
  )
  // Teksten kan være flyttet væk fra sin normale plads (lx/ly) - den følger stadig symbolet.
  const lx = e.lx ?? 0
  const ly = e.ly ?? 0
  const grabLabel = (ev: ReactPointerEvent) => onHandle(ev, 'label')
  const above = label && (
    <LinesAbove text={label} x={e.x + w / 2 + lx} y={e.y - 8 + ly} size={e.kind === 'breaker' ? 15 : 18} onGrab={grabLabel} />
  )

  if (e.kind === 'line') {
    const x2 = e.x2 ?? e.x
    const y2 = e.y2 ?? e.y
    return (
      <g style={{ cursor: 'move' }} onPointerDown={onMove}>
        <line x1={e.x} y1={e.y} x2={x2} y2={y2} stroke="transparent" strokeWidth={22} />
        <line
          x1={e.x} y1={e.y} x2={x2} y2={y2}
          stroke={selected ? '#F97316' : BLUE}
          strokeWidth={stroke}
          strokeDasharray={e.dashed ? '18 12' : undefined}
          markerEnd={e.arrow ? 'url(#diagram-arrow)' : undefined}
        />
        {label && <LinesAbove text={label} x={(e.x + x2) / 2 + lx} y={Math.min(e.y, y2) - 8 + ly} size={15} onGrab={grabLabel} />}
        {selected && handle(e.x, e.y, 'p1')}
        {selected && handle(x2, y2, 'p2')}
      </g>
    )
  }

  if (e.kind === 'text') {
    const size = e.size || 18
    const lines = label.split('\n')
    const width = Math.max(...lines.map((l) => l.length)) * size * 0.56
    return (
      <g style={{ cursor: 'move' }} onPointerDown={onMove}>
        <rect
          x={e.x - 6} y={e.y} width={width + 12} height={size * 1.2 * lines.length + size * 0.5}
          fill={e.highlight ? '#FFFF00' : 'transparent'}
          stroke={selected ? '#F97316' : 'transparent'} strokeDasharray="6 4" strokeWidth={2}
        />
        {lines.map((line, i) => (
          <text key={i} x={e.x} y={e.y + size * 1.2 * (i + 1)} fontSize={size} fontWeight={e.bold ? 700 : 400} fill="#111">{line}</text>
        ))}
      </g>
    )
  }

  const outline = selected && (
    <rect x={e.x - 6} y={e.y - 6} width={w + 12} height={h + 12} fill="none" stroke="#F97316" strokeWidth={2} strokeDasharray="6 4" />
  )

  return (
    <g style={{ cursor: 'move' }} onPointerDown={onMove}>
      <rect x={e.x} y={e.y} width={w} height={h} fill="transparent" />
      {e.kind === 'meter' && (
        <>
          <rect x={e.x} y={e.y} width={w} height={h} fill="#fff" stroke={BLUE} strokeWidth={stroke} />
          <text x={e.x + w / 2} y={e.y + h / 2} textAnchor="middle" dominantBaseline="central" fontSize={Math.max(12, h * 0.35)} fill="#111">M</text>
          {above}
        </>
      )}
      {e.kind === 'inverter' && (
        <>
          <rect x={e.x} y={e.y} width={w} height={h} fill="#fff" stroke={BLUE} strokeWidth={stroke} />
          <line x1={e.x + w * 0.1} y1={e.y + h * 0.9} x2={e.x + w * 0.9} y2={e.y + h * 0.1} stroke={BLUE} strokeWidth={1.5} />
          <text x={e.x + w * 0.27} y={e.y + h * 0.3} textAnchor="middle" dominantBaseline="central" fontSize={Math.max(10, h * 0.28)} fontWeight={700}>~</text>
          <text x={e.x + w * 0.73} y={e.y + h * 0.72} textAnchor="middle" dominantBaseline="central" fontSize={Math.max(10, h * 0.28)} fontWeight={700}>=</text>
          {above}
        </>
      )}
      {e.kind === 'breaker' && (
        <>
          <line x1={e.x} y1={e.y + h * 0.75} x2={e.x + w * 0.35} y2={e.y + h * 0.75} stroke={BLUE} strokeWidth={stroke} />
          <line x1={e.x + w * 0.35} y1={e.y + h * 0.75} x2={e.x + w * 0.65} y2={e.y + h * 0.15} stroke={BLUE} strokeWidth={stroke} />
          <line x1={e.x + w * 0.72} y1={e.y + h * 0.75} x2={e.x + w} y2={e.y + h * 0.75} stroke={BLUE} strokeWidth={stroke} />
          {above}
        </>
      )}
      {e.kind === 'box' && (
        <>
          <rect x={e.x} y={e.y} width={w} height={h} fill="#fff" stroke={BLUE} strokeWidth={6} />
          {label && (
            <text
              x={e.x + w / 2 + lx} y={e.y + h / 2 + ly} textAnchor="middle" dominantBaseline="central" fontSize={18} fill="#111"
              style={{ cursor: 'grab' }}
              onPointerDown={(ev) => onHandle(ev, 'label')}
              {...(label.length * 10 > w - 16 ? { textLength: w - 16, lengthAdjust: 'spacingAndGlyphs' as const } : {})}
            >
              {label}
            </text>
          )}
        </>
      )}
      {outline}
      {selected && handle(e.x + w, e.y + h, 'resize')}
    </g>
  )
}

/**
 * Tekst over et symbol - flere linjer stables opad, som i PDF'en (DiagramPdfService.DrawLinesAbove).
 * Den kan gribes for sig selv og trækkes væk fra symbolet, når to tekster står oven i hinanden.
 */
function LinesAbove({
  text,
  x,
  y,
  size,
  onGrab
}: {
  text: string
  x: number
  y: number
  size: number
  onGrab: (event: ReactPointerEvent) => void
}) {
  const lines = text.split('\n')
  return (
    <g style={{ cursor: 'grab' }} onPointerDown={onGrab}>
      {lines.map((line, i) => (
        <text key={i} x={x} y={y - (lines.length - 1 - i) * size * 1.2} textAnchor="middle" fontSize={size} fill="#111">{line}</text>
      ))}
    </g>
  )
}

/** Små tegninger i paletten. */
function PaletteIcon({ kind }: { kind: string }) {
  const s = { stroke: BLUE, strokeWidth: 3, fill: 'none' }
  switch (kind) {
    case 'meter':
      return <><rect x={16} y={6} width={28} height={28} {...s} fill="#fff" /><text x={30} y={25} textAnchor="middle" fontSize={14}>M</text></>
    case 'breaker':
      return <><line x1={4} y1={30} x2={22} y2={30} {...s} /><line x1={22} y1={30} x2={38} y2={12} {...s} /><line x1={42} y1={30} x2={56} y2={30} {...s} /></>
    case 'inverter':
      return <><rect x={16} y={6} width={28} height={28} {...s} fill="#fff" /><line x1={19} y1={31} x2={41} y2={9} stroke={BLUE} strokeWidth={1} /><text x={23} y={19} fontSize={10}>~</text><text x={33} y={31} fontSize={10}>=</text></>
    case 'box':
      return <rect x={4} y={10} width={52} height={20} {...s} strokeWidth={4} fill="#fff" />
    case 'line':
      return <line x1={4} y1={20} x2={56} y2={20} {...s} />
    case 'vline':
      return <line x1={30} y1={2} x2={30} y2={38} {...s} />
    case 'busbar':
      return <line x1={30} y1={2} x2={30} y2={38} {...s} strokeWidth={6} />
    case 'border':
      return <line x1={30} y1={2} x2={30} y2={38} {...s} strokeDasharray="6 4" />
    case 'arrow':
      return <><line x1={4} y1={20} x2={46} y2={20} {...s} /><path d="M 44 13 L 56 20 L 44 27 z" fill={BLUE} /></>
    case 'highlight':
      return <><rect x={6} y={10} width={48} height={20} fill="#FFFF00" /><text x={30} y={25} textAnchor="middle" fontSize={12}>Abc</text></>
    default:
      return <text x={30} y={26} textAnchor="middle" fontSize={16}>Abc</text>
  }
}
